import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EstadoEmergenciaMedica, IEmergencia } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { EmergenciasService } from '../../../../../auxiliares/servicios/http/emergencias.service';
import { estadoDe, estaFinalizada } from '../../estado-emergencia';
import {
  AsignarEmergenciaDialogComponent,
  IAsignarEmergenciaDialogData,
} from './asignar-emergencia-dialog/asignar-emergencia-dialog.component';
import {
  CancelarEmergenciaDialogComponent,
  ICancelarEmergenciaDialogData,
} from './cancelar-emergencia-dialog/cancelar-emergencia-dialog.component';
import {
  FinalizarEmergenciaDialogComponent,
  IFinalizarEmergenciaDialogData,
} from './finalizar-emergencia-dialog/finalizar-emergencia-dialog.component';
import { SelectorHospitalEmergenciaComponent } from '../selector-hospital-emergencia/selector-hospital-emergencia.component';
import {
  FormularioDialogService,
  TamanoFormulario,
} from '../../../../../auxiliares/servicios/formulario-dialog.service';

/** Estados desde los que se puede marcar la llegada al lugar. */
const ESTADOS_LLEGADA_DESTINO: EstadoEmergenciaMedica[] = [
  'Asignada',
  'Reasignada',
  'En tránsito',
];

/**
 * Cambios de estado manuales de una emergencia médica. Cada acción abre su
 * diálogo, llama al endpoint de la API (que valida el estado previo y el
 * permiso, y registra el evento de seguimiento), avisa y devuelve `true` si
 * la emergencia cambió. Los `puedeX` combinan estado y permiso para mostrar u
 * ocultar la acción en listados y detalle.
 */
@Injectable({ providedIn: 'root' })
export class AccionesEmergenciaService {
  private dialog = inject(MatDialog);
  private formularios = inject(FormularioDialogService);
  private dialogService = inject(DialogService);
  private helper = inject(HelperService);
  private emergenciasService = inject(EmergenciasService);

  // Estado + permiso

  private activa(e?: IEmergencia | null): boolean {
    return !!e?._id && !estaFinalizada(e);
  }

  /** Sólo los auxilios llevan móvil: las de seguimiento no se asignan. */
  public puedeAsignar(e?: IEmergencia | null): boolean {
    return (
      this.activa(e) &&
      !!e!.esAuxilio &&
      !e!.asignada &&
      this.helper.tienePermiso('Emergencias Médicas - Editar')
    );
  }

  public puedeReasignar(e?: IEmergencia | null): boolean {
    return (
      this.activa(e) &&
      !!e!.asignada &&
      this.helper.tienePermiso('Emergencias Médicas - Reasignar')
    );
  }

  public puedeCancelar(e?: IEmergencia | null): boolean {
    return (
      this.activa(e) &&
      this.helper.tienePermiso('Emergencias Médicas - Cancelar')
    );
  }

  public puedeFinalizar(e?: IEmergencia | null): boolean {
    return (
      this.activa(e) &&
      this.helper.tienePermiso('Emergencias Médicas - Finalizar')
    );
  }

  public puedeRumboHospital(e?: IEmergencia | null): boolean {
    return (
      this.activa(e) &&
      !!e!.asignada &&
      estadoDe(e) !== 'Rumbo a hospital' &&
      estadoDe(e) !== 'Llegada a hospital' &&
      this.helper.tienePermiso('Emergencias Médicas - Editar')
    );
  }

  public puedeLlegadaDestino(e?: IEmergencia | null): boolean {
    return (
      this.activa(e) &&
      ESTADOS_LLEGADA_DESTINO.includes(estadoDe(e) as EstadoEmergenciaMedica) &&
      this.helper.tienePermiso('Emergencias Médicas - Editar')
    );
  }

  // Acciones

  public asignar(e: IEmergencia): Promise<boolean> {
    return this.abrirAsignacion(e, false);
  }

  public reasignar(e: IEmergencia): Promise<boolean> {
    return this.abrirAsignacion(e, true);
  }

  private async abrirAsignacion(
    e: IEmergencia,
    reasignar: boolean,
  ): Promise<boolean> {
    const data: IAsignarEmergenciaDialogData = {
      emergencia: e,
      reasignar,
      guardar: (datos) =>
        reasignar
          ? this.emergenciasService.reasignar(e._id!, datos)
          : this.emergenciasService.asignar(e._id!, datos),
    };
    const ok = await this.abrir(
      AsignarEmergenciaDialogComponent,
      data,
      'mediano',
    );
    if (ok) {
      this.helper.notifSuccess(
        reasignar ? 'Emergencia reasignada' : 'Emergencia asignada',
      );
    }
    return ok;
  }

  public async cancelar(e: IEmergencia): Promise<boolean> {
    const data: ICancelarEmergenciaDialogData = {
      emergencia: e,
      guardar: (datos) => this.emergenciasService.cancelar(e._id!, datos),
    };
    const ok = await this.abrir(
      CancelarEmergenciaDialogComponent,
      data,
      'chico',
    );
    if (ok) this.helper.notifSuccess('Emergencia cancelada');
    return ok;
  }

  public async finalizar(e: IEmergencia): Promise<boolean> {
    const data: IFinalizarEmergenciaDialogData = {
      emergencia: e,
      guardar: (datos) => this.emergenciasService.finalizar(e._id!, datos),
    };
    const ok = await this.abrir(
      FinalizarEmergenciaDialogComponent,
      data,
      'chico',
    );
    if (ok) this.helper.notifSuccess('Emergencia finalizada');
    return ok;
  }

  public async rumboHospital(e: IEmergencia): Promise<boolean> {
    const ref = this.dialog.open(SelectorHospitalEmergenciaComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: { emergencia: e },
    });
    const idHospital = (await firstValueFrom(ref.afterClosed())) as
      | string
      | undefined;
    if (!idHospital) return false;
    try {
      await this.emergenciasService.rumboHospital(e._id!, idHospital);
      this.helper.notifSuccess('Ambulancia rumbo a hospital');
      return true;
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
      return false;
    }
  }

  public async llegadaDestino(e: IEmergencia): Promise<boolean> {
    const ok = await this.dialogService.confirm(
      'Llegada a destino',
      `¿Confirmás que la ambulancia llegó al lugar de la emergencia ${e.codigo ?? ''}?`,
      'Confirmar',
      'Cancelar',
    );
    if (!ok) return false;
    try {
      await this.emergenciasService.llegadaDestino(e._id!);
      this.helper.notifSuccess('Llegada a destino registrada');
      return true;
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
      return false;
    }
  }

  private async abrir<D>(
    componente: any,
    data: D,
    tamano: TamanoFormulario,
  ): Promise<boolean> {
    const ref = this.formularios.abrir(componente, { tamano, data });
    return !!(await firstValueFrom(ref.afterClosed()));
  }
}
